/**
 * The Central's automatic assistant identity (D631, doc 25 §1 Q-361 and §11) — governed key `central.assistant`.
 *
 * The assistant is called **Vello** and opens every conversation with a standard DISCLOSURE line (it is an automatic assistant,
 * and a person is one word away). Both are settings: the display name, the languages the assistant serves, and the disclosure
 * line in each of those languages. A platform version is the default for every country; a country version replaces it WHOLE
 * (a country's legal reading of "you are talking to an automatic assistant" is its own text).
 *
 * A settings-kernel key (D393, ADR-0060): platform and country scope, OVERRIDABLE (the most specific effective version wins),
 * four eyes on every change, versioned, effective-dated, content-hashed. The registry default is the standard Vello identity, so
 * publishing nothing changes no country.
 *
 * What a lower scope can NEVER do: drop the disclosure. Every enabled language needs its line (`DISCLOSURE_MISSING_FOR_LANGUAGE`),
 * and a line for a language that is not enabled is refused (dead text a reviewer would think was live). A version the reader
 * cannot verify or parse fails to the BUILT-IN default, never to "no disclosure" (`CENTRAL_ASSISTANT_INVALID_DEFAULTED`).
 *
 * Neutral: no country is named; the languages are the platform's four Admin/app languages (BCP-47 primary subtags).
 */
import { COUNTRY_RE, effectiveBucket, isObj, isPlainText, onlyMembers, scopedSetting } from './central-ai-shared.js';
/** The governed key. */
export const CENTRAL_ASSISTANT_KEY = 'central.assistant';
/** The languages an assistant identity can be written in (the platform's four content languages). */
export const CENTRAL_ASSISTANT_LANGUAGES = ['pt', 'en', 'es', 'it'];
/** The standard Vello identity: the registry default, and what an unverifiable version falls back to. */
export const DEFAULT_CENTRAL_ASSISTANT = {
    displayName: 'Vello',
    languages: ['pt', 'en', 'es'],
    disclosure: {
        pt: 'Você está falando com o Vello, o assistente automático. A qualquer momento, escreva atendente para falar com uma pessoa.',
        en: 'You are talking to Vello, the automatic assistant of Vello. At any time, write agent to speak with a person.',
        es: 'Estás hablando con Vello, el asistente automático de Vello. En cualquier momento, escribe agente para hablar con una persona.',
    },
};
export const CENTRAL_ASSISTANT_REASON_CODES = ['CENTRAL_ASSISTANT_GOVERNED', 'CENTRAL_ASSISTANT_DEFAULT', 'CENTRAL_ASSISTANT_INVALID_DEFAULTED'];
export const CENTRAL_ASSISTANT_NAME_BOUNDS = { min: 1, max: 40 };
export const CENTRAL_ASSISTANT_DISCLOSURE_BOUNDS = { min: 20, max: 500 };
const isLanguage = (v) => typeof v === 'string' && CENTRAL_ASSISTANT_LANGUAGES.includes(v);
/** The stable issue of a value that is not an assistant identity, or undefined when it is one. */
export function centralAssistantIssue(v) {
    if (!isObj(v))
        return 'NOT_AN_OBJECT';
    if (!onlyMembers(v, ['displayName', 'languages', 'disclosure']) || !('displayName' in v) || !('languages' in v) || !('disclosure' in v))
        return 'UNKNOWN_OR_MISSING_MEMBER';
    if (!isPlainText(v.displayName, CENTRAL_ASSISTANT_NAME_BOUNDS.min, CENTRAL_ASSISTANT_NAME_BOUNDS.max))
        return 'INVALID_DISPLAY_NAME';
    if (!Array.isArray(v.languages) || v.languages.length < 1 || v.languages.length > CENTRAL_ASSISTANT_LANGUAGES.length)
        return 'INVALID_LANGUAGES';
    if (!v.languages.every(isLanguage) || new Set(v.languages).size !== v.languages.length)
        return 'INVALID_LANGUAGES';
    if (!isObj(v.disclosure))
        return 'DISCLOSURE_NOT_AN_OBJECT';
    for (const lang of Object.keys(v.disclosure)) {
        if (!isLanguage(lang))
            return 'DISCLOSURE_UNKNOWN_LANGUAGE';
        if (!v.languages.includes(lang))
            return 'DISCLOSURE_FOR_DISABLED_LANGUAGE';
    }
    for (const lang of v.languages) {
        const line = v.disclosure[lang];
        if (line === undefined)
            return 'DISCLOSURE_MISSING_FOR_LANGUAGE';
        if (!isPlainText(line, CENTRAL_ASSISTANT_DISCLOSURE_BOUNDS.min, CENTRAL_ASSISTANT_DISCLOSURE_BOUNDS.max))
            return 'INVALID_DISCLOSURE';
    }
    return undefined;
}
export function validCentralAssistantValue(v) {
    return centralAssistantIssue(v) === undefined;
}
/** The value normalised: languages keep their order, the disclosure keeps one line per enabled language in that order. */
function normalise(v) {
    return {
        displayName: v.displayName,
        languages: [...v.languages],
        disclosure: Object.fromEntries(v.languages.map((l) => [l, v.disclosure[l]])),
    };
}
/** Build a valid version (platform or country scope). Throws a RangeError on a malformed scope ref or value. */
export function centralAssistantSetting(input) {
    const issue = centralAssistantIssue(input.value);
    if (issue !== undefined)
        throw new RangeError(`invalid Central assistant identity: ${issue}`);
    return scopedSetting({ key: CENTRAL_ASSISTANT_KEY, scope: input.scope, ...(input.scopeRef !== undefined ? { scopeRef: input.scopeRef } : {}), value: normalise(input.value), version: input.version, effectiveFrom: input.effectiveFrom, ...(input.effectiveTo !== undefined ? { effectiveTo: input.effectiveTo } : {}), mode: 'OVERRIDABLE' });
}
/**
 * The identity in force for `country` at `at`, over the GOVERNED reader's versions: the country's version, else the platform's,
 * else the registry default. Never throws and never returns a value without a disclosure.
 */
export function resolveCentralAssistant(settings, country, at) {
    const defaulted = (reasonCode, suffix) => ({ value: DEFAULT_CENTRAL_ASSISTANT, reasonCode, policyVersion: `${CENTRAL_ASSISTANT_KEY}@${suffix}` });
    if (!COUNTRY_RE.test(country))
        return defaulted('CENTRAL_ASSISTANT_INVALID_DEFAULTED', 'invalid-defaulted');
    const bucket = effectiveBucket(settings, CENTRAL_ASSISTANT_KEY, 'country', country, at) ?? effectiveBucket(settings, CENTRAL_ASSISTANT_KEY, 'platform', undefined, at);
    if (bucket === undefined)
        return defaulted('CENTRAL_ASSISTANT_DEFAULT', 'default');
    if (!validCentralAssistantValue(bucket.value))
        return defaulted('CENTRAL_ASSISTANT_INVALID_DEFAULTED', 'invalid-defaulted');
    return {
        value: normalise(bucket.value),
        reasonCode: 'CENTRAL_ASSISTANT_GOVERNED',
        policyVersion: bucket.policyVersion,
        ...(bucket.contentHash !== undefined ? { contentHash: bucket.contentHash } : {}),
        ...(bucket.changeId !== undefined ? { changeId: bucket.changeId } : {}),
    };
}
/**
 * The disclosure line to open a conversation with, in `language` when it is enabled, else in the first enabled language (the
 * identity's preferred one). Always returns a line: a valid identity has one for every enabled language.
 */
export function centralAssistantDisclosure(value, language) {
    const chosen = isLanguage(language) && value.languages.includes(language) ? language : value.languages[0];
    return { language: chosen, text: value.disclosure[chosen] };
}
//# sourceMappingURL=central-assistant-policy.js.map